// @givmo/observability — Sentry event/breadcrumb scrubbing.
//
// The load-bearing privacy invariant from 03 §2: NO email / amount / token / PII may leave
// the browser in a Sentry event OR a breadcrumb. These hooks are wired into `Sentry.init`
// by `init.ts` and run on every captured event and every recorded breadcrumb, applying the
// `redaction.ts` deny-list to every field that can carry user data.
//
// We scrub defensively over the WHOLE event rather than trusting Sentry's own data-handling:
// `sendDefaultPii` is off (see init.ts) and server-side scrubbing is the second layer, but
// the client `beforeSend` is the authoritative gate — if it doesn't strip it, it leaves the
// device. We therefore redact: the message, the exception values/messages, breadcrumbs
// (message + data), request (url / query / headers / cookies / body), extra, contexts, tags,
// and the user object (email/username/ip dropped, id kept as it is an opaque identifier).
import type { Breadcrumb, ErrorEvent, Event } from '@sentry/react';
import { redactString, redactValue, isSensitiveKey } from './redaction';

/** Redact a single breadcrumb's free-text + structured data. Pure — returns a new crumb. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const scrubbed: Breadcrumb = { ...breadcrumb };
  if (typeof scrubbed.message === 'string') {
    scrubbed.message = redactString(scrubbed.message);
  }
  if (scrubbed.data !== undefined) {
    scrubbed.data = redactValue(scrubbed.data) as Breadcrumb['data'];
  }
  return scrubbed;
}

/**
 * Redact every PII-bearing surface of a Sentry event. Operates on a shallow clone of the
 * event and the nested containers it rewrites, so the caller's object is never mutated.
 * Accepts the broad `Event` type so it can serve both the `ErrorEvent`-typed `beforeSend`
 * and any future transaction hook; `beforeSend` re-narrows the return.
 */
export function scrubEvent<E extends Event>(event: E): E {
  const scrubbed: E = { ...event };

  // Top-level message.
  if (typeof scrubbed.message === 'string') {
    scrubbed.message = redactString(scrubbed.message);
  }

  // Exception values — the human-readable error string is the most common PII leak
  // (e.g. "user jane@x.com not found").
  if (scrubbed.exception?.values) {
    scrubbed.exception = {
      ...scrubbed.exception,
      values: scrubbed.exception.values.map((value) => ({
        ...value,
        value: typeof value.value === 'string' ? redactString(value.value) : value.value,
      })),
    };
  }

  // logentry (parameterized message).
  if (scrubbed.logentry?.message) {
    scrubbed.logentry = {
      ...scrubbed.logentry,
      message: redactString(scrubbed.logentry.message),
    };
  }

  // Breadcrumbs.
  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map(scrubBreadcrumb);
  }

  // Request: URL/query/body/headers/cookies all can carry tokens + PII.
  if (scrubbed.request) {
    const request = { ...scrubbed.request };
    if (typeof request.url === 'string') request.url = redactString(request.url);
    if (request.query_string !== undefined) {
      request.query_string = redactValue(request.query_string) as typeof request.query_string;
    }
    if (request.data !== undefined) request.data = redactValue(request.data);
    if (request.headers) {
      request.headers = redactValue(request.headers) as Record<string, string>;
    }
    if (request.cookies) {
      request.cookies = redactValue(request.cookies) as Record<string, string>;
    }
    scrubbed.request = request;
  }

  // Arbitrary structured bags.
  if (scrubbed.extra) {
    scrubbed.extra = redactValue(scrubbed.extra) as Event['extra'];
  }
  if (scrubbed.contexts) {
    scrubbed.contexts = redactValue(scrubbed.contexts) as Event['contexts'];
  }

  // Tags: keys are queryable in Sentry, so redact by key name AND value.
  if (scrubbed.tags) {
    const tags: Record<string, unknown> = {};
    for (const key of Object.keys(scrubbed.tags)) {
      const original = scrubbed.tags[key];
      tags[key] = isSensitiveKey(key)
        ? '[REDACTED]'
        : typeof original === 'string'
          ? redactString(original)
          : original;
    }
    scrubbed.tags = tags as Event['tags'];
  }

  // User: drop the directly-identifying fields; keep the opaque `id` for diagnosis.
  if (scrubbed.user) {
    const { id } = scrubbed.user;
    scrubbed.user = id === undefined ? {} : { id };
  }

  // Top-level string/array fields that can carry PII verbatim:
  //  - `transaction` is a manually-named span (e.g. "/donate?email=…").
  //  - `fingerprint` is a string[] used for grouping — may embed a route + query.
  //  - `server_name` can leak a host that encodes user/tenant info.
  if (typeof scrubbed.transaction === 'string') {
    scrubbed.transaction = redactString(scrubbed.transaction);
  }
  if (scrubbed.fingerprint) {
    scrubbed.fingerprint = scrubbed.fingerprint.map(redactString);
  }
  if (typeof scrubbed.server_name === 'string') {
    scrubbed.server_name = redactString(scrubbed.server_name);
  }

  return scrubbed;
}

/**
 * `Sentry.init({ beforeSend })` hook. Narrowed to `ErrorEvent` to match the SDK signature.
 * Never drops the event (returns the scrubbed event, not null) — scrubbing, not sampling.
 * The SDK passes a second `hint` argument we deliberately ignore (it carries the raw,
 * un-scrubbed originating exception/response — exactly what must not be read here).
 */
export function beforeSend(event: ErrorEvent): ErrorEvent {
  return scrubEvent(event);
}

/** `Sentry.init({ beforeBreadcrumb })` hook. The optional `hint` is deliberately ignored. */
export function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  return scrubBreadcrumb(breadcrumb);
}
